import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Users } from 'lucide-react';
import { BackButton } from '@/components/common/BackButton';
import { DataTable, FEW_ROWS_TABLE_FRAME_CLASS } from '@/components/common/DataTable';
import { DetailPanel } from '@/components/common/DetailPanel';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { ExportButton } from '@/components/common/ExportButton';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { useHistoryBack } from '@/hooks/useHistoryBack';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import {
  donationBatchQueryOptions,
  donationMembersQueryOptions,
  exportDonationMembers,
} from '../api/marketPromotionP2.api';
import { DonationBatchActionDialog } from '../components/DonationBatchActionDialog';
import { DonationBatchSummaryPanel } from '../components/DonationBatchSummaryPanel';
import { DonationBatchTrail } from '../components/DonationBatchTrail';
import { DONATION_MEMBER_COLUMN_ID, getDonationMemberColumns } from '../components/MpP2Columns';
import { SummarySideLayout } from '../components/SummarySideLayout';
import { getDonationBatchPanel, useDonationBatchAction } from '../hooks/useDonationBatchAction';
import type { DonationBatchMembersSearch } from '../types/marketPromotionP2.types';

interface DonationBatchMembersPageProps {
  batchId: number;
  search: DonationBatchMembersSearch;
}

export function DonationBatchMembersPage({ batchId, search }: DonationBatchMembersPageProps) {
  const navigate = useNavigate();
  const goBack = useHistoryBack(() => void navigate({ to: search.from }));
  useRememberViewedRow('donationBatches', batchId);
  const panel = getDonationBatchPanel(search.from);
  const { action, actingBatch, closeAction } = useDonationBatchAction(panel);
  const ActionIcon = action.icon;

  const batchQuery = useQuery(donationBatchQueryOptions(batchId));
  const membersQuery = useQuery(donationMembersQueryOptions(batchId));
  const columns = useMemo(() => getDonationMemberColumns(), []);

  const batch = batchQuery.data;
  const members = membersQuery.data;
  const loadError = batchQuery.error ?? membersQuery.error;

  function renderContent() {
    if (loadError) {
      return (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load this batch"
          onRetry={() => {
            void batchQuery.refetch();
            void membersQuery.refetch();
          }}
        />
      );
    }
    if (batch === null) {
      return (
        <EmptyState
          title="This batch was not found"
          description="It may have been removed. Go back to the list to pick another batch."
        />
      );
    }
    return (
      <SummarySideLayout summary={<DonationBatchSummaryPanel batch={batch} members={members} />}>
        <DonationBatchTrail batch={batch} />
        <DetailPanel title="ZMs" icon={Users}>
          <DataTable
            data={members ?? []}
            columns={columns}
            isLoading={members === undefined}
            stickyColumnId={DONATION_MEMBER_COLUMN_ID}
            emptyMessage="No ZM is on this batch"
            className={FEW_ROWS_TABLE_FRAME_CLASS}
          />
        </DetailPanel>
      </SummarySideLayout>
    );
  }

  return (
    <div className="flex flex-col gap-page-gap">
      <PageHeader
        title="Batch Details"
        leading={<BackButton onClick={goBack} />}
        actions={
          <>
            <ExportButton
              onExport={() => exportDonationMembers(batchId)}
              disabled={members === undefined || members.length === 0}
            />
            {batch && action.when(batch) ? (
              <Button onClick={() => action.onClick(batch)}>
                <ActionIcon className="size-4" aria-hidden />
                {action.label}
              </Button>
            ) : null}
          </>
        }
      />
      {renderContent()}
      <DonationBatchActionDialog panel={panel} batch={actingBatch} onClose={closeAction} />
    </div>
  );
}
